// components/shared-components/MaintenanceScreen
//
// Full-screen "We'll be right back" overlay. It is driven entirely by the
// `maintenance` redux slice, which the axios response interceptor toggles when
// the API returns a 503 carrying the maintenance marker. While it is showing it
// polls the (exempt) admin maintenance endpoint every ~30s and auto-recovers as
// soon as maintenance is turned off again.
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setMaintenance,
  clearMaintenance,
} from "store/slices/maintenanceSlice";
import MaintenanceService from "services/MaintenanceService";

const POLL_INTERVAL_MS = 30000;

const DEFAULT_MESSAGE =
  "We're performing some scheduled maintenance right now. We'll be back shortly — thanks for your patience.";

const BRAND = "#305CDE";

const WrenchGearIcon = () => (
  <svg
    width="44"
    height="44"
    viewBox="0 0 24 24"
    fill="none"
    stroke={BRAND}
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{
      transformOrigin: "70% 30%",
      animation: "lmAdminRock 2.4s ease-in-out infinite",
    }}
  >
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
  </svg>
);

const MaintenanceScreen = () => {
  const dispatch = useDispatch();
  const { active, message } = useSelector((state) => state.maintenance);

  useEffect(() => {
    if (!active) return undefined;

    let cancelled = false;

    const poll = async () => {
      try {
        const res = await MaintenanceService.getState();
        if (cancelled) return;
        // The service returns the response body directly; be defensive about
        // shape in case of a wrapper/encryption layer.
        const state = res?.data && "enabled" in res.data ? res.data : res;
        const enabled = state?.enabled;
        if (enabled === false) {
          dispatch(clearMaintenance());
        } else if (enabled === true) {
          dispatch(setMaintenance(state?.message || ""));
        }
      } catch (e) {
        // Still unreachable / not authorized — keep showing and retry next tick.
      }
    };

    const timer = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [active, dispatch]);

  if (!active) return null;

  return (
    <div
      role="alertdialog"
      aria-live="polite"
      aria-label="Service under maintenance"
      className="fixed inset-0 z-[10050] flex items-center justify-center px-6"
      style={{
        background:
          "radial-gradient(circle at 50% 0%, rgba(48,92,222,0.12), #F6F6F6 60%)",
      }}
    >
      <style>{`
        @keyframes lmAdminRise { from { opacity:0; transform:translateY(12px);} }
        @keyframes lmAdminRock { 0%,100%{transform:rotate(-8deg);} 50%{transform:rotate(8deg);} }
        @keyframes lmAdminPulse { 0%{transform:scale(1);opacity:.7;} 100%{transform:scale(1.35);opacity:0;} }
      `}</style>

      <div
        className="w-full max-w-md rounded-3xl bg-white px-8 py-12 text-center"
        style={{
          boxShadow:
            "0 20px 60px -15px rgba(48,92,222,0.25), 0 8px 24px -12px rgba(0,0,0,0.15)",
          animation: "lmAdminRise 0.5s cubic-bezier(0.16,1,0.3,1) both",
        }}
      >
        <img
          src="/img/logo.png"
          alt="Lensman"
          className="mx-auto mb-8 h-11 w-auto object-contain"
        />

        <div
          className="relative mx-auto mb-7 flex h-24 w-24 items-center justify-center rounded-full"
          style={{ background: "rgba(48,92,222,0.1)" }}
        >
          <span
            className="pointer-events-none absolute inset-0 rounded-full"
            style={{
              border: "2px solid rgba(48,92,222,0.35)",
              animation: "lmAdminPulse 1.8s ease-out infinite",
            }}
          />
          <WrenchGearIcon />
        </div>

        <h1 className="mb-3 text-2xl font-semibold tracking-tight text-[#312828] sm:text-[28px]">
          We&apos;ll be right back
        </h1>

        <p className="mx-auto mb-9 max-w-sm text-[15px] leading-relaxed text-gray-500">
          {message && message.trim().length > 0 ? message : DEFAULT_MESSAGE}
        </p>

        <div className="flex items-center justify-center gap-2 text-sm font-medium text-gray-400">
          <span className="relative flex h-2.5 w-2.5">
            <span
              className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-75"
              style={{ background: BRAND }}
            ></span>
            <span
              className="relative inline-flex h-2.5 w-2.5 rounded-full"
              style={{ background: BRAND }}
            ></span>
          </span>
          <span>Retrying automatically&hellip;</span>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceScreen;
